// Simple hash-based router

import { icon } from './icons';

type RouteHandler = (params: Record<string, string>) => HTMLElement | Promise<HTMLElement>;

interface Route {
  pattern: RegExp;
  handler: RouteHandler;
  paramNames: string[];
}

const routes: Route[] = [];
let currentView: HTMLElement | null = null;
let appContainer: HTMLElement | null = null;

export function registerRoute(path: string, handler: RouteHandler): void {
  // Convert path pattern to regex
  // Supports: /path, /path/:param, /@:name
  const paramNames: string[] = [];
  const pattern = path
    .replace(/\//g, '\\/')
    .replace(/:([^/]+)/g, (_, name) => {
      paramNames.push(name);
      return '([^/]+)';
    });

  routes.push({
    pattern: new RegExp(`^${pattern}$`),
    handler,
    paramNames,
  });
}

export function navigate(path: string): void {
  window.location.hash = path;
}

export function getCurrentRoute(): string {
  return window.location.hash.slice(1) || '/';
}

function parseRoute(hash: string): { handler: RouteHandler; params: Record<string, string> } | null {
  const path = hash.slice(1) || '/';

  for (const route of routes) {
    const match = path.match(route.pattern);
    if (match) {
      const params: Record<string, string> = {};
      route.paramNames.forEach((name, index) => {
        params[name] = decodeURIComponent(match[index + 1]);
      });
      return { handler: route.handler, params };
    }
  }

  return null;
}

async function handleRouteChange(): Promise<void> {
  if (!appContainer) return;

  const route = parseRoute(window.location.hash);

  if (route) {
    try {
      const newView = await route.handler(route.params);
      if (currentView) {
        currentView.remove();
      }
      appContainer.appendChild(newView);
      currentView = newView;
    } catch (err) {
      console.error('Route handler error:', err);
      showError('Failed to load page');
    }
  } else {
    // 404 - redirect to home
    navigate('/');
  }
}

function showError(message: string): void {
  if (!appContainer) return;

  if (currentView) {
    currentView.remove();
  }

  const errorView = document.createElement('div');
  errorView.className = 'empty';
  errorView.innerHTML = `
    <div class="empty__icon">${icon('error', 48)}</div>
    <p class="empty__text">${message}</p>
    <button class="btn" style="margin-top: 20px;">Go Home</button>
  `;

  errorView.querySelector('button')?.addEventListener('click', () => navigate('/'));

  appContainer.appendChild(errorView);
  currentView = errorView;
}

export function initRouter(container: HTMLElement): void {
  appContainer = container;

  window.addEventListener('hashchange', handleRouteChange);

  // Handle initial route
  handleRouteChange();
}

// Loading indicator helper
export function createLoadingIndicator(): HTMLElement {
  const loading = document.createElement('div');
  loading.className = 'loading';
  loading.innerHTML = `
    <div class="loading__spinner"></div>
    <p class="loading__text">Loading</p>
  `;
  return loading;
}
